import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs';

// Alerts
import Swal from 'sweetalert2';

// Services
import { AuthService } from '../services/auth/auth.service';
import { LoginErrorHandlerService } from '../services/auth/login-error-handler.service';
import { LanguageService } from '../services/language/language.service';
import { SeoService } from '../services/seo/seo.service';
import { SelectCountriesService } from '../services/countries/select-countries.service';

// Models
import { User } from '../models/user.model';
import { LoginType } from '../services/auth/models/auth-login.models';
import { MicrosoftProfile } from '../services/auth/models/auth-microsoft-login.models';

// Translate
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';

// Social login
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';

// Crypt
import { hashPassword, hashThirdPartyId } from '../services/auth/auth-hash';

import { PrivateRoutes } from '../config/config.routes';
import { MixpanelService } from '../services/analytics/mixpanel.service';
import { mixpanelEvent } from '../config/mixpanel-events';

/**
 * Declare plugins so then you can run it
 */
declare function init_plugins(): void;

/** The part of Google Identity Services (loaded by the social login library) used here */
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          renderButton(
            parent: HTMLElement,
            options: { type: string; width: string },
          ): void;
        };
      };
    };
  }
}

/** Group validator: `pass` and `confirmPassword` must match */
function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const pass = group.get('pass')?.value;
  const confirmPassword = group.get('confirmPassword')?.value;
  return pass === confirmPassword ? null : { passwordsMatch: true };
}

/**
 * Component to manage the register page
 */
@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, ReactiveFormsModule, TranslocoPipe],
})
export class RegisterComponent implements OnInit {
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly authService = inject(AuthService);
  private readonly loginErrorHandler = inject(LoginErrorHandlerService);
  private readonly languageService = inject(LanguageService);
  private readonly transloco = inject(TranslocoService);
  private readonly mixpanel = inject(MixpanelService);
  private readonly seoService = inject(SeoService);
  private readonly title = inject(Title);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly language = this.languageService.getLanguage();
  private readonly userType = this.readUserType();

  readonly countries = inject(SelectCountriesService).getCountries();
  readonly loading = signal(false);
  private googleLoginWrapper?: HTMLElement;

  readonly form = inject(NonNullableFormBuilder).group(
    {
      first_name: ['', [Validators.required, Validators.minLength(3)]],
      last_name: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      country: ['', Validators.required],
      pass: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(6)]],
      conditions: [false],
    },
    { validators: passwordsMatch },
  );

  constructor() {
    this.socialAuthService.authState
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe((googleUser) => {
        this.socialAuthService.signOut().catch(() => undefined);
        this.register(this.fromGoogleUser(googleUser), 'GOOGLE');
      });
  }

  ngOnInit() {
    init_plugins();

    // SEO
    const title = this.transloco.translate('SIGN-UP.SIGN-UP');
    const description = this.transloco.translate('COMMON.DESCRIPTION');
    this.title.setTitle(title);
    this.seoService.generateTags({
      title: title,
      description: description,
      slug: 'sign-up',
    });

    localStorage.removeItem('Type');
  }

  /**
   * Email/password sign up
   */
  registerWithLab4u() {
    if (this.loading()) return;
    if (this.form.invalid) {
      const message = this.transloco.translate('SIGN-UP.ERROR.INVALID-DATA');
      Swal.fire('Error', message, 'error');
      return;
    }

    this.register(this.fromForm(), 'LAB4U');
  }

  /**
   * Google only allows its own rendered button to start the sign in, so this
   * clicks a hidden one to keep our custom styled button
   */
  registerWithGoogle() {
    if (this.loading()) return;
    this.renderHiddenGoogleButton()?.click();
  }

  registerWithMicrosoft() {
    if (this.loading()) return;
    this.loading.set(true);
    this.authService
      .microsoftLogin()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) =>
          this.register(this.fromMicrosoftProfile(profile), 'MICROSOFT'),
        error: (err) => this.onMicrosoftError(err),
      });
  }

  /**
   * Function to change language localStorage
   * @param language Variable to save the actual language
   */
  changeLanguage(language: string) {
    if (language === 'es' || language === 'en') {
      this.languageService.applyLanguage(language);
      window.location.reload();
    }
  }

  private register(user: User, loginType: LoginType) {
    this.loading.set(true);
    this.authService
      .newUser(user)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.login(user, loginType),
        error: () => this.loading.set(false),
      });
  }

  private login(user: User, loginType: LoginType) {
    this.authService
      .login(user)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.triggerSignUpSucceededEvent(loginType);
          this.navigateAfterSignUp();
        },
        error: (err) => this.onLoginAfterSignUpError(err),
      });
  }

  private navigateAfterSignUp() {
    this.router
      .navigateByUrl(PrivateRoutes.HOME)
      .then((navigated) => {
        if (!navigated) this.loading.set(false);
      })
      .catch(() => this.loading.set(false));
  }

  /** MSAL popup errors; the handler ignores a closed popup */
  private onMicrosoftError(err: unknown) {
    this.loading.set(false);
    this.loginErrorHandler.handle(err, { loginType: 'MICROSOFT' });
  }

  /**
   * The account was just created, so a failed login is a backend or connection
   * problem: the Login page messages (e.g. "not registered") don't apply here
   */
  private onLoginAfterSignUpError(err: unknown) {
    console.error('Login after sign up failed', err);
    this.loading.set(false);
    const message = this.transloco.translate('SIGN-IN.ERROR.TITLE');
    Swal.fire('Error', message, 'error');
  }

  private fromForm(): User {
    const { first_name, last_name, email, country, pass } =
      this.form.getRawValue();
    const user = this.newSignUpUser('LAB4U', 'lab4chemistry');
    user.first_name = first_name;
    user.last_name = last_name;
    user.email = email.toLowerCase();
    user.country = country;
    user.type = this.userType;
    user.password = hashPassword(user.email, pass);
    return user;
  }

  private fromGoogleUser(googleUser: SocialUser): User {
    const user = this.newSignUpUser('GOOGLE');
    user.first_name = googleUser.name;
    user.last_name = '';
    user.email = googleUser.email;
    user.image = googleUser.photoUrl;
    user.third_party_id = hashThirdPartyId(googleUser.id);
    return user;
  }

  private fromMicrosoftProfile(profile: MicrosoftProfile): User {
    const user = this.newSignUpUser('MICROSOFT');
    user.email = profile.email;
    user.first_name = profile.givenName || profile.email.split('@')[0];
    user.last_name = profile.surname || '';
    user.third_party_id = profile.graphId;
    return user;
  }

  /**
   * Fields shared by every sign up, whatever the provider
   * @param app Email sign ups have always been registered as 'lab4chemistry', SSO ones as 'Portal'
   */
  private newSignUpUser(loginType: LoginType, app = 'Portal'): User {
    const user = new User();
    user.language = this.language;
    user.login_type = loginType;
    user.user_type = this.userType;
    user.gender = 'other';
    user.password = '';
    user.biz_type = 'B2C';
    user.app = app;
    user.isGuest = false;
    return user;
  }

  private readUserType(): string {
    const storedType = localStorage.getItem('Type');
    return storedType ? JSON.parse(storedType) : '';
  }

  /** Rendered once and reused, removed when the component is destroyed */
  private renderHiddenGoogleButton(): HTMLElement | null {
    if (!window.google) return null;

    if (!this.googleLoginWrapper) {
      const wrapper = document.createElement('div');
      wrapper.style.display = 'none';
      wrapper.classList.add('custom-google-button');
      document.body.appendChild(wrapper);
      window.google.accounts.id.renderButton(wrapper, {
        type: 'icon',
        width: '200',
      });
      this.googleLoginWrapper = wrapper;
      this.destroyRef.onDestroy(() => wrapper.remove());
    }

    return this.googleLoginWrapper.querySelector<HTMLElement>(
      'div[role=button]',
    );
  }

  private triggerSignUpSucceededEvent(loginType: LoginType) {
    this.mixpanel.track(mixpanelEvent.SIGN_UP, {
      source_name: mixpanelEvent.SOURCE_NAME,
      feature_name: 'Miscelanea',
      view_name: 'Auth',
      event_version: 1,
      event_id: 297,
      event_type: 'Success',

      login_type: loginType,
    });
  }
}
